// src/components/Admin/UserManager/UserList.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, TextField, Select, MenuItem, InputLabel, FormControl,
    TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Pagination,
    Stack, Typography, Chip, Button, Divider, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import api from '../../../api/axios';
import UserItem from './UserItem';

const ROWS_PER_PAGE = 15;

const roleLabel = (role) => {
    if (role === 'master') return '마스터';
    if (role === 'admin') return '어드민';
    return '유저';
};

const roleColor = (role) => {
    if (role === 'master') return 'warning';
    if (role === 'admin') return 'primary';
    return 'default';
};

export default function UserList({ userInfo }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [sortBy, setSortBy] = useState('username');
    const [page, setPage] = useState(1);

    /* ─────────── API ─────────── */
    const fetchUsers = useCallback(() => {
        api.get('/api/management/users/')
            .then((res) => setUsers(res.data))
            .catch(() => alert('유저 정보를 불러오는 데 실패했습니다.'));
    }, []);

    useEffect(fetchUsers, [fetchUsers]);

    /* ───────── 필터·정렬 ───────── */
    const filteredUsers = useMemo(
        () => users
            .filter((u) => (
                !search.trim()
                || ((u.nickname || '').toLowerCase().includes(search.toLowerCase()))
                || ((u.username || '').toLowerCase().includes(search.toLowerCase()))
            ))
            .filter((u) => roleFilter === 'all' || u.role === roleFilter)
            .sort((a, b) => {
                if (sortBy === 'points') return (b.points || 0) - (a.points || 0);
                if (sortBy === 'date_joined') return new Date(b.date_joined) - new Date(a.date_joined);
                if (sortBy === 'last_login') return new Date(b.last_login) - new Date(a.last_login);
                return (a.nickname || a.username || '').localeCompare(b.nickname || b.username || '');
            }),
        [users, search, roleFilter, sortBy],
    );

    /* ───────── 페이지 ───────── */
    const paginated = filteredUsers.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
    const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE) || 1;
    useEffect(() => setPage(1), [search, roleFilter, sortBy]);

    /* ───────── 모바일 카드용 액션 (✅ 실제 엔드포인트로 수정) ───────── */
    const changeRole = async (userId, role) => {
        try {
            await api.post(`/api/management/users/${userId}/role/`, { role });
            alert('권한이 변경되었습니다.');
            fetchUsers();
        } catch (e) {
            console.error(e);
            alert('권한 변경 실패');
        }
    };

    // ✅ 포인트: amount는 양수, type은 earn/use, description 필수
    const adjustPoints = async (userId, signedAmount) => {
        const n = Number(signedAmount);
        if (!Number.isFinite(n) || n === 0) return;

        const amount = Math.abs(n);
        const type = n > 0 ? 'earn' : 'use';

        const desc = window.prompt('사유를 입력하세요 (필수)\n예) 이벤트 참여로 지급 / 규정 위반으로 회수');
        if (!desc || !desc.trim()) {
            alert('사유는 필수입니다.');
            return;
        }

        try {
            await api.post(`/api/management/users/${userId}/point/`, {
                amount,
                type,
                description: desc.trim(),
            });
            alert('Leaf🌿가 반영되었습니다.');
            fetchUsers();
        } catch (e) {
            console.error(e);
            alert('Leaf🌿 반영 실패');
        }
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {/* 검색 / 필터 바 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', minWidth: 0 }}>
                <TextField
                    label="닉네임"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: isMobile ? '100%' : 220 }}
                />

                <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 160 }}>
                    <InputLabel>권한</InputLabel>
                    <Select
                        value={roleFilter}
                        label="권한"
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="user">유저</MenuItem>
                        <MenuItem value="admin">어드민</MenuItem>
                        <MenuItem value="master">마스터</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 160 }}>
                    <InputLabel>정렬 기준</InputLabel>
                    <Select
                        value={sortBy}
                        label="정렬 기준"
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <MenuItem value="username">닉네임순</MenuItem>
                        <MenuItem value="points">Leaf🌿순</MenuItem>
                        <MenuItem value="last_login">최근접속순</MenuItem>
                        <MenuItem value="date_joined">가입일순</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ✅ 모바일: 카드형 */}
            {isMobile ? (
                <Stack spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
                    {paginated.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary' }}>🔍 조건에 맞는 유저가 없습니다.</Typography>
                        </Paper>
                    ) : (
                        paginated.map((u) => (
                            <Paper
                                key={u.id}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                {/* 상단: 닉네임 / role */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 800,
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {u.nickname || u.username}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        color={roleColor(u.role)}
                                        label={roleLabel(u.role)}
                                        sx={{ flex: 'none' }}
                                    />
                                </Box>

                                <Typography sx={{ mt: 0.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                                    Leaf🌿: <b>{(u.points ?? 0).toLocaleString()}</b>
                                </Typography>

                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip size="small" variant="outlined" label={`가입: ${dayjs(u.date_joined).format('YYYY-MM-DD')}`} />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`최근: ${u.last_login ? dayjs(u.last_login).format('YYYY-MM-DD') : '-'}`}
                                    />
                                    <Chip size="small" variant="outlined" label={`상태: ${u.is_banned ? '정지' : '정상'}`} />
                                </Box>

                                <Divider sx={{ my: 1.25 }} />

                                {/* 권한 변경 */}
                                <Stack spacing={1} sx={{ width: '100%' }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>권한 변경</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant={u.role === 'user' ? 'contained' : 'outlined'}
                                            onClick={() => changeRole(u.id, 'user')}
                                            fullWidth
                                        >
                                            유저
                                        </Button>
                                        <Button
                                            size="small"
                                            variant={u.role === 'admin' ? 'contained' : 'outlined'}
                                            onClick={() => changeRole(u.id, 'admin')}
                                            fullWidth
                                        >
                                            어드민
                                        </Button>
                                        <Button
                                            size="small"
                                            color="warning"
                                            variant={u.role === 'master' ? 'contained' : 'outlined'}
                                            onClick={() => changeRole(u.id, 'master')}
                                            fullWidth
                                        >
                                            마스터
                                        </Button>
                                    </Stack>

                                    {/* 포인트 지급/차감 */}
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mt: 0.5 }}>Leaf🌿 지급/차감</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const v = window.prompt('지급할 Leaf🌿를 입력하세요 (예: 100)');
                                                if (v == null) return;
                                                adjustPoints(u.id, Number(v));
                                            }}
                                            fullWidth
                                        >
                                            지급
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                const v = window.prompt('차감할 Leaf🌿를 입력하세요 (예: 100)');
                                                if (v == null) return;
                                                adjustPoints(u.id, -Number(v));
                                            }}
                                            fullWidth
                                        >
                                            차감
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))
                    )}

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                shape="rounded"
                                color="primary"
                                size="small"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </Stack>
            ) : (
                /* ✅ PC: 기존 테이블 유지 (UserItem 그대로) */
                <>
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            '& tr:hover': { bgcolor: 'grey.100' },
                            '& td, & th': {
                                borderBottom: '1px solid #eee',
                                textAlign: 'center',
                                p: 1.25,
                                whiteSpace: 'nowrap',
                            },
                            '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={130}>닉네임</TableCell>
                                    <TableCell width={120}>Leaf🌿</TableCell>
                                    <TableCell width={160}>가입일</TableCell>
                                    <TableCell width={160}>최근 접속일</TableCell>
                                    <TableCell width={90}>상태</TableCell>
                                    <TableCell width={120}>권한</TableCell>
                                    <TableCell width={240}>관리</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ py: 5 }}>
                                            🔍 조건에 맞는 유저가 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((u) => (
                                        <UserItem
                                            key={u.id}
                                            user={u}
                                            userInfo={userInfo}
                                            refreshUsers={fetchUsers}
                                        >
                                            <TableCell align="center">
                                                {dayjs(u.date_joined).format('YYYY-MM-DD')}
                                            </TableCell>

                                            <TableCell align="center">
                                                {u.last_login ? dayjs(u.last_login).format('YYYY-MM-DD') : '-'}
                                            </TableCell>
                                        </UserItem>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                shape="rounded"
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
